/**
 * ItemPurchaseSummary — the cost-and-purchasing header for one canonical
 * inventory item.
 *
 * It exists to stop four different ideas being rendered as one "Price" and one
 * "Vendor" field. Each gets its own labelled slot, always in the same order:
 *
 *   Current inventory cost   what inventory and recipes are valued at, in the
 *                            item's own count unit, with the costing method
 *                            named underneath
 *   Purchase options         how many vendor products are linked, their price
 *                            range, and how many still need unit review
 *   Preferred vendor         the purchasing preference
 *   Last purchased           the most recent completed purchase
 *   Best comparable price    the lowest normalized price that can actually be
 *                            compared
 *
 * Nothing here invents a number. A cost that cannot be computed renders as
 * "Current inventory cost unavailable" plus the reason — never $0.00 — and a
 * vendor comparison that fails leaves the rest of the card (and the item
 * editor hosting it) fully usable.
 *
 * Compact list rows use ItemCostSummaryCell instead — same util, same labels,
 * far less vertical space.
 *
 * Props:
 *   summary       - buildItemCostSummary() output (utils/itemCostSummary)
 *   variant       - 'editor' (bordered card) | 'plain' (no chrome, denser)
 *   loading       - render skeletons instead of values (never $0.00)
 *   error         - message shown in place of the vendor sections only
 *   onRetry       - retry action beside `error`
 *   onViewOptions - opens the purchase-options content DIRECTLY (no
 *                   intermediate description screen). Omit to hide the button.
 *   onChoosePreferred - opens the same content for picking a preferred vendor
 *   headline      - false to omit the item name/category line (hosts that
 *                   already show it, e.g. a modal header)
 */
import React from 'react';
import {
  formatMoney,
  formatUnitPrice,
  formatPriceRange,
  formatPurchaseDate,
  describePurchaseOptions,
  bestPriceLabel,
} from '../utils/itemCostSummary';

const C = {
  ink: '#3E2723',
  body: '#5A4A42',
  muted: '#8B7B73',
  line: '#E8DDD0',
  hairline: '#F1EAE2',
  surface: '#FEFCFA',
  well: '#FBF8F5',
  amber: '#8A5A00',
  amberBg: '#FFF4E0',
  amberLine: '#E6B866',
  green: '#2E7D32',
  greenBg: '#E8F5E9',
  blue: '#0d47a1',
  blueBg: '#E3F2FD',
  danger: '#b71c1c',
  dangerBg: '#FDECEA',
};

const styles = {
  wrap: {
    fontFamily: 'Open Sans, sans-serif',
    color: C.body,
    background: C.surface,
    border: `1.5px solid ${C.line}`,
    borderRadius: 12,
    padding: '14px 16px',
  },
  rowWrap: { fontFamily: 'Open Sans, sans-serif', color: C.body },
  headline: { marginBottom: 12 },
  itemName: { fontSize: '1.05rem', fontWeight: 700, color: C.ink, lineHeight: 1.25 },
  itemMeta: { fontSize: '0.8rem', color: C.muted, marginTop: 2 },

  costBlock: {
    borderBottom: `1px solid ${C.hairline}`,
    paddingBottom: 12,
    marginBottom: 12,
  },
  label: {
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: C.muted,
    margin: 0,
  },
  costValue: { fontSize: '1.6rem', fontWeight: 700, color: C.ink, lineHeight: 1.15, margin: '2px 0 0' },
  costValueSmall: { fontSize: '1.05rem', fontWeight: 700, color: C.ink, lineHeight: 1.2, margin: '2px 0 0' },
  costUnavailable: { fontSize: '1rem', fontWeight: 700, color: C.muted, lineHeight: 1.2, margin: '2px 0 0' },
  method: { fontSize: '0.8rem', color: C.body, marginTop: 3 },
  reason: { fontSize: '0.78rem', color: C.muted, marginTop: 3, maxWidth: '42ch' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, margin: 0 },
  slot: { minWidth: 0 },
  slotValue: { fontSize: '0.92rem', fontWeight: 700, color: C.ink, marginTop: 3, wordBreak: 'break-word' },
  slotLine: { fontSize: '0.8rem', color: C.body, marginTop: 1 },
  slotMuted: { fontSize: '0.8rem', color: C.muted, marginTop: 1 },
  slotEmpty: { fontSize: '0.88rem', color: C.muted, marginTop: 3, fontStyle: 'italic' },

  badges: { display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 },
  badge: (tone) => ({
    display: 'inline-block',
    padding: '1px 7px',
    borderRadius: 999,
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    background: tone.bg,
    color: tone.fg,
    border: `1px solid ${tone.line || 'transparent'}`,
  }),

  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minHeight: 40, padding: '8px 16px', borderRadius: 9,
    fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
    background: C.body, color: '#fff', border: 'none',
  },
  secondaryBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minHeight: 40, padding: '8px 14px', borderRadius: 9,
    fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
    background: '#fff', color: C.body, border: `1.5px solid #D5C9C0`,
  },
  error: {
    padding: '8px 12px', borderRadius: 8, marginBottom: 10,
    background: C.dangerBg, color: C.danger, border: '1px solid #f5c2bd',
    fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
  },
  skeleton: (w) => ({
    display: 'block', width: w, height: 14, borderRadius: 6,
    background: '#EFE7DF', marginTop: 6,
  }),
};

const TONES = {
  preferred: { bg: C.amberBg, fg: C.amber, line: C.amberLine },
  best: { bg: C.greenBg, fg: C.green },
  last: { bg: C.blueBg, fg: C.blue },
  warn: { bg: C.amberBg, fg: C.amber, line: C.amberLine },
  neutral: { bg: '#F5F0EB', fg: C.muted, line: '#D9CEC2' },
};

/**
 * Status badges for one purchase option, given the item's summary. Text
 * carries the meaning — colour only reinforces it — so a badge still reads
 * correctly in monochrome or to a screen reader.
 */
export function optionBadges(option, summary) {
  if (!option || !summary) return [];
  const badges = [];
  if (option.is_preferred) badges.push({ key: 'preferred', label: 'Preferred', tone: TONES.preferred });
  if (summary.best?.option && String(summary.best.option.id) === String(option.id)) {
    badges.push({ key: 'best', label: summary.best.stale ? 'Lowest known' : 'Best price', tone: TONES.best });
  }
  if (summary.lastPurchase?.optionId != null
    && String(summary.lastPurchase.optionId) === String(option.id)) {
    badges.push({ key: 'last', label: 'Last purchased', tone: TONES.last });
  }
  if (option.conversion_status === 'unresolved') {
    badges.push({ key: 'unit', label: 'Unit missing', tone: TONES.warn });
  }
  if (option.verification_status === 'rejected' || option.is_approved === false) {
    badges.push({ key: 'review', label: 'Needs review', tone: TONES.warn });
  }
  if (option.is_hidden_from_allocation) {
    badges.push({ key: 'hidden', label: 'Unavailable', tone: TONES.neutral });
  }
  return badges;
}

const Badges = ({ items }) => (
  items && items.length ? (
    <div style={styles.badges}>
      {items.map((badge) => (
        <span key={badge.key} style={styles.badge(badge.tone)}>{badge.label}</span>
      ))}
    </div>
  ) : null
);

/** One labelled slot. `dt`/`dd` so the label is programmatically tied to its value. */
const Slot = ({ label, children, testId }) => (
  <div style={styles.slot} data-testid={testId}>
    <dt style={styles.label}>{label}</dt>
    <dd style={{ margin: 0 }}>{children}</dd>
  </div>
);

const Empty = ({ children }) => <div style={styles.slotEmpty}>{children}</div>;

/** Vendor product identity + prices, shared by the preferred/last/best slots. */
const VendorLines = ({ vendorLabel, vendorItemName, packDescription, packagePrice, unitCost, unit, extra }) => (
  <>
    <div style={styles.slotValue}>{vendorLabel || 'Vendor not linked'}</div>
    {vendorItemName && <div style={styles.slotLine}>{vendorItemName}</div>}
    {(packDescription || packagePrice != null) && (
      <div style={styles.slotMuted}>
        {[
          packDescription || null,
          packagePrice != null ? `${formatMoney(packagePrice)} per package` : null,
        ].filter(Boolean).join(' · ')}
      </div>
    )}
    {unitCost != null && <div style={styles.slotLine}>{formatUnitPrice(unitCost, unit)}</div>}
    {extra}
  </>
);

export default function ItemPurchaseSummary({
  summary,
  variant = 'editor',
  loading = false,
  error = null,
  onRetry,
  onViewOptions,
  onChoosePreferred,
  headline = true,
}) {
  if (!summary) return null;

  const { item, countUnit, currentCost, preferred, lastPurchase, best, optionsSummary } = summary;
  const compact = variant !== 'editor';

  const costNode = loading ? (
    <span style={styles.skeleton(120)} data-testid="ips-cost-skeleton" aria-hidden="true" />
  ) : currentCost.available ? (
    <>
      <div style={compact ? styles.costValueSmall : styles.costValue} data-testid="ips-cost-value">
        {formatUnitPrice(currentCost.amount, countUnit)}
      </div>
      <div style={styles.method} data-testid="ips-costing-method">{currentCost.method.label}</div>
      {currentCost.fellBack && currentCost.reasonText && (
        <div style={styles.reason}>{currentCost.reasonText}</div>
      )}
    </>
  ) : (
    <>
      <div style={styles.costUnavailable} data-testid="ips-cost-unavailable">
        Current inventory cost unavailable
      </div>
      <div style={styles.method}>{currentCost.method.label}</div>
      <div style={styles.reason}>{currentCost.reasonText}</div>
    </>
  );

  const optionsNode = loading ? (
    <span style={styles.skeleton(150)} aria-hidden="true" />
  ) : (
    <>
      <div style={styles.slotValue} data-testid="ips-options-count">
        {describePurchaseOptions(optionsSummary)}
      </div>
      {optionsSummary.range && (
        <div style={styles.slotLine} data-testid="ips-options-range">
          {formatPriceRange(optionsSummary.range, countUnit)}
        </div>
      )}
    </>
  );

  const vendorSections = error ? (
    <div style={styles.error} role="alert">
      <span>Purchase-option pricing could not be loaded.</span>
      {onRetry && (
        <button type="button" style={{ ...styles.secondaryBtn, minHeight: 30, padding: '4px 10px', fontSize: '0.78rem' }} onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  ) : (
    <dl style={styles.grid}>
      <Slot label="Purchase options" testId="ips-options">{optionsNode}</Slot>

      <Slot label="Preferred vendor" testId="ips-preferred">
        {loading ? <span style={styles.skeleton(130)} aria-hidden="true" /> : preferred ? (
          <VendorLines
            vendorLabel={preferred.vendorLabel}
            vendorItemName={preferred.vendorItemName}
            packDescription={preferred.packDescription}
            packagePrice={preferred.packagePrice}
            unitCost={preferred.unitCost}
            unit={countUnit}
            extra={<Badges items={[{ key: 'preferred', label: 'Preferred', tone: TONES.preferred }]} />}
          />
        ) : (
          <>
            <Empty>No preferred vendor selected</Empty>
            {onChoosePreferred && (
              <button
                type="button"
                style={{ ...styles.secondaryBtn, minHeight: 32, padding: '5px 11px', fontSize: '0.78rem', marginTop: 6 }}
                onClick={onChoosePreferred}
              >
                Choose preferred vendor
              </button>
            )}
          </>
        )}
      </Slot>

      <Slot label="Last purchased" testId="ips-last">
        {loading ? <span style={styles.skeleton(130)} aria-hidden="true" /> : lastPurchase ? (
          <VendorLines
            vendorLabel={lastPurchase.vendorLabel}
            vendorItemName={lastPurchase.vendorItemName}
            packDescription={lastPurchase.packDescription}
            packagePrice={lastPurchase.packagePrice}
            unitCost={lastPurchase.normalizedUnitCost}
            unit={countUnit}
            extra={formatPurchaseDate(lastPurchase.purchasedAt) ? (
              <div style={styles.slotMuted}>Purchased {formatPurchaseDate(lastPurchase.purchasedAt)}</div>
            ) : null}
          />
        ) : (
          <Empty>No purchase history</Empty>
        )}
      </Slot>

      <Slot label={bestPriceLabel(best)} testId="ips-best">
        {loading ? <span style={styles.skeleton(130)} aria-hidden="true" /> : best ? (
          <VendorLines
            vendorLabel={best.vendorLabel}
            vendorItemName={best.vendorItemName}
            packDescription={best.packDescription}
            packagePrice={best.packagePrice}
            unitCost={best.unitCost}
            unit={countUnit}
            extra={best.stale ? (
              <div style={styles.slotMuted}>Price may be outdated</div>
            ) : null}
          />
        ) : (
          <>
            <Empty>No comparable vendor prices</Empty>
            <div style={styles.reason}>
              Pack size or unit conversion is missing for one or more purchase options.
            </div>
          </>
        )}
      </Slot>
    </dl>
  );

  return (
    <section
      style={compact ? styles.rowWrap : styles.wrap}
      data-testid="item-purchase-summary"
      aria-label={`Cost and purchasing for ${item?.description || 'item'}`}
    >
      {headline && (
        <header style={styles.headline}>
          <div style={styles.itemName}>{item?.description || 'Item'}</div>
          <div style={styles.itemMeta}>
            {[
              item?.category || null,
              countUnit ? `Inventory unit: ${countUnit}` : null,
            ].filter(Boolean).join(' · ')}
          </div>
        </header>
      )}

      <div style={styles.costBlock}>
        <div style={styles.label}>Current inventory cost</div>
        {costNode}
      </div>

      {vendorSections}

      {(onViewOptions || (onChoosePreferred && preferred)) && (
        <div style={styles.actions}>
          {onViewOptions && (
            <button
              type="button"
              style={styles.primaryBtn}
              onClick={onViewOptions}
              data-testid="ips-view-options"
            >
              View purchase options
            </button>
          )}
          {onChoosePreferred && preferred && (
            <button type="button" style={styles.secondaryBtn} onClick={onChoosePreferred}>
              Change preferred vendor
            </button>
          )}
        </div>
      )}
    </section>
  );
}
